export const CONTENT_CARD_COL_SPAN = 2;

/** Matches ProductListStyles gridTemplateColumns breakpoint column counts. */
const GRID_COLS = [2, 2, 3, 4, 4, 4] as const;

export type EditorialWidgetGridPosition = {
    colSpan?: number | number[];
    rowStart?: number | number[];
    colStart?: number | number[];
    rowSpan?: number;
    fontSize?: string | string[];
};

type WidgetSlot = {
    minProducts: readonly [number, number];
    position: EditorialWidgetGridPosition;
};

function expandValues(value: number | number[] | undefined, fallback = 1): number[] {
    const values = Array.isArray(value) ? [...value] : [value ?? fallback];
    while (values.length < GRID_COLS.length) {
        values.push(values[values.length - 1]);
    }
    return values;
}

function collapseValues(values: number[], wasArray: boolean): number | number[] {
    return wasArray ? values : values[0];
}

function valueAtBreakpoint(value: number | number[] | undefined, sm: boolean): number {
    const values = expandValues(value);
    return values[sm ? 1 : 0];
}

/** Widen to 2 columns; keep left slots on the left and right slots on the right. */
function widenForContentCard(position: EditorialWidgetGridPosition): EditorialWidgetGridPosition {
    const colSpanIsArray = Array.isArray(position.colSpan);
    const colStartIsArray = Array.isArray(position.colStart);

    const colSpans = expandValues(position.colSpan);
    const newColSpans = colSpans.map((span, index) =>
        Math.min(Math.max(span, CONTENT_CARD_COL_SPAN), GRID_COLS[index])
    );

    let newColStarts: number[];
    if (position.colStart !== undefined) {
        const colStarts = expandValues(position.colStart);
        newColStarts = colStarts.map((start, index) => {
            const spanGain = newColSpans[index] - colSpans[index];
            return spanGain > 0 ? Math.max(1, start - spanGain) : start;
        });
    } else {
        newColStarts = GRID_COLS.map(() => 1);
    }

    return {
        ...position,
        colSpan: collapseValues(newColSpans, colSpanIsArray),
        colStart: collapseValues(newColStarts, position.colStart === undefined || colStartIsArray),
        rowSpan: 1,
    };
}

function getMinProducts(slot: WidgetSlot, sm: boolean, isContentCard: boolean): number {
    const threshold = slot.minProducts[sm ? 1 : 0];
    if (!isContentCard) {
        return threshold;
    }

    return valueAtBreakpoint(slot.position.colSpan, sm) >= CONTENT_CARD_COL_SPAN
        ? threshold
        : threshold - 1;
}

export function resolveWidgetPosition(
    products: number,
    index: number,
    widgetSize: string | undefined,
    sm: boolean,
    isContentCard: boolean
): EditorialWidgetGridPosition | null {
    const config = widgetSize ? WIDGET_POSITIONS[widgetSize] : undefined;
    const slot = config?.slots[index];

    if (!slot || products < getMinProducts(slot, sm, isContentCard)) {
        return null;
    }

    return isContentCard ? widenForContentCard(slot.position) : slot.position;
}

export const WIDGET_POSITIONS: Record<string, { slots: readonly WidgetSlot[] }> = {
    Small: {
        slots: [
            {
                // The minimum number of products required to display the first widget on mobile or desktop.
                minProducts: [4, 7],
                position: {
                    colSpan: [2, 1, 1],
                    rowStart: [3, 2, 2, 2],
                    fontSize: ["mobileH1", "tabletH3", "desktopH3"],
                },
            },
            {
                minProducts: [8, 14],
                position: {
                    colSpan: [2, 1, 1],
                    colStart: [1, 3, 3, 4],
                    rowStart: [6, 4, 4],
                    fontSize: ["mobileH1", "tabletH3", "desktopH3"],
                },
            },
            {
                minProducts: [12, 21],
                position: {
                    colSpan: [2, 1, 1],
                    colStart: 1,
                    rowStart: [9, 6, 6, 6],
                    fontSize: ["mobileH1", "tabletH3", "desktopH3"],
                },
            },
            {
                minProducts: [16, 28],
                position: {
                    colSpan: [2, 1, 1],
                    colStart: [1, 3, 3, 4],
                    rowStart: [12, 8, 8],
                    fontSize: ["mobileH1", "tabletH3", "desktopH3"],
                },
            },
        ],
    },
    Tall: {
        slots: [
            {
                minProducts: [4, 10],
                position: {
                    colSpan: [2, 1, 1],
                    rowSpan: 2,
                    rowStart: [3, 2, 2, 2],
                    fontSize: ["mobileH1", "desktopH2", "desktopH2"],
                },
            },
            {
                minProducts: [8, 20],
                position: {
                    colSpan: [2, 1, 1],
                    rowSpan: 2,
                    colStart: [1, 3, 3, 4],
                    rowStart: [7, 5, 5],
                    fontSize: ["mobileH1", "desktopH2", "desktopH2"],
                },
            },
            {
                minProducts: [12, 30],
                position: {
                    colSpan: [2, 1, 1],
                    rowSpan: 2,
                    rowStart: [11, 8, 8],
                    fontSize: ["mobileH1", "desktopH2", "desktopH2"],
                },
            },
            {
                minProducts: [16, 40],
                position: {
                    colSpan: [2, 1, 1],
                    rowSpan: 2,
                    colStart: [1, 3, 3, 4],
                    rowStart: [15, 11, 11],
                    fontSize: ["mobileH1", "desktopH2", "desktopH2"],
                },
            },
        ],
    },
    Big: {
        slots: [
            {
                minProducts: [4, 8],
                position: {
                    colSpan: 2,
                    rowSpan: 2,
                    rowStart: [3, 2, 2, 2],
                    fontSize: ["mobileH1", "desktopH1", "desktopH1"],
                },
            },
            {
                minProducts: [8, 16],
                position: {
                    colSpan: 2,
                    rowSpan: 2,
                    colStart: [1, 1, 2, 3, 3],
                    rowStart: [7, 5, 5],
                    fontSize: ["mobileH1", "desktopH1", "desktopH1"],
                },
            },
            {
                minProducts: [12, 24],
                position: {
                    colSpan: 2,
                    rowSpan: 2,
                    rowStart: [11, 8, 8],
                    fontSize: ["mobileH1", "desktopH1", "desktopH1"],
                },
            },
            {
                minProducts: [16, 32],
                position: {
                    colSpan: 2,
                    rowSpan: 2,
                    colStart: [1, 1, 2, 3, 3],
                    rowStart: [15, 11, 11],
                    fontSize: ["mobileH1", "desktopH1", "desktopH1"],
                },
            },
        ],
    },
};
